import React, { useState, useEffect, useMemo } from "react";
import {
  useAddUserMutation,
  useGetUserQuery,
  useUpdateUserMutation,
} from "../../store";
import { useAlert } from "../../context/AlertContext";
import { useFirmData } from "../../Hooks/useFirmData";
import Loader from "./Loader";
import UserForm from "./UserForm";
import useDrawerTransition from "../../Hooks/useDrawerTransition";

// Styles for overlay and panel — modern drawer
const overlayClass =
  "fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity";
const panelBaseClass =
  "fixed top-0 right-0 h-full w-full sm:max-w-lg bg-white shadow-xl z-50 transform transition-transform duration-300 ease-out overflow-hidden flex flex-col";

// Generic Drawer wrapper — modern tasarım
export const Drawer = ({ open, onClose, children, title = "Düzenle" }) => {
  // Kapalı pozisyonda mount → iki frame sonra açılır; kapanışta içerik
  // dondurularak çıkış animasyonu boyunca sabit kalır.
  const { mounted, entered, data: view } = useDrawerTransition(open, { children, title });
  if (!mounted) return null;
  return (
  <>
    <div
      className={`${overlayClass} duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
      onClick={onClose}
      aria-hidden
    />
    <div
      className={`${panelBaseClass} ${entered ? "translate-x-0" : "translate-x-full"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-title"
    >
      <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-slate-200 bg-slate-50/80">
        <h2 id="drawer-title" className="text-xl font-semibold text-slate-800">
          {view.title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors"
          aria-label="Kapat"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-6">{view.children}</div>
    </div>
  </>
  );
};

/** Rol adını Türkçe göstermek için */
const ROLE_DISPLAY_NAME = {
  storage: "Depo",
  dealer: "Bayi",
  customer: "Müşteri",
  machineworker: "Makine İşçisi",
};

const SUBROLE_DISPLAY_NAME = {
  management: "Yönetim",
  service: "Servis",
  playmaker: "Plasiyer",
  accounting: "Muhasebe",
  depo: "Depo", // bayi (dealer) subRole'ü
  manufacturer: "Üretim",
  rom: "Bölge Operasyon Yöneticisi",
  fs: "Saha Sorumlusu",
  tl: "Ekip Lideri",
  tech: "Teknisyen",
  op: "Operatör",
};

/** defaultFirm: { firmName, firmType, _id } — firma kullanıcılar sekmesinden açıldığında verilir; firma ve role göre roller filtrelenir */
export const UserCreateDrawer = ({ open, onClose, token, refetch, defaultFirm, mutation }) => {
  // Kapalı pozisyonda mount → kayarak açılır; kapanışta çıkış animasyonu
  // bitene kadar monte kalır (form sıfırlaması da o ana ertelenir).
  const { mounted, entered } = useDrawerTransition(open);
  const [input, setInput] = useState({
    role: "",
    firmName: "",
    name: "",
    lastName: "",
    email: "",
    tel: "",
    cardId: "",
    nfcCardId: "",
    isActive: true,
    subRole: "",
  });
  const [confirmStep, setConfirmStep] = useState(false);
  const { showSuccess, showError } = useAlert();
  const defaultMutation = useAddUserMutation();
  const [addUser, resultAdd] = mutation || defaultMutation;

  const firmTypeFromFirm = defaultFirm?.firmType || null;

  useEffect(() => {
    if (open && defaultFirm?.firmName) {
      setInput((prev) => ({ ...prev, firmName: defaultFirm.firmName }));
    }
    if (!mounted) {
      setInput({
        role: "",
        firmName: defaultFirm?.firmName || "",
        name: "",
        lastName: "",
        email: "",
        tel: "",
        cardId: "",
        nfcCardId: "",
        isActive: true,
        subRole: "",
      });
      setConfirmStep(false);
    }
  }, [open, mounted, defaultFirm?.firmName]);

  useEffect(() => {
    if (resultAdd.isSuccess) {
      showSuccess("Kullanıcı oluşturuldu!");
      refetch?.();
      onClose();
    } else if (resultAdd.isError) {
      showError(resultAdd.error?.data?.message || "Kullanıcı oluşturulurken hata oluştu.");
    }
    // Sadece mutation sonucu değişince çalışsın; refetch/onClose deps'te olmasın yoksa refetch
    // tamamlanınca re-render → effect tekrar çalışır → sürekli refetch döngüsü olur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultAdd.isSuccess, resultAdd.isError, resultAdd.error]);

  const handleSubmit = () => {
    const userPayload = {
      ...input,
      // role ve subRole artık doğrudan sistem değerleri (storage, dealer, vb.)
      subRole: input.subRole || (input.role === "customer" ? "Admin" : ""),
    };
    // Template'den gelen permissions varsa ekle
    if (input.templatePermissions) {
      userPayload.permissions = input.templatePermissions;
      delete userPayload.templatePermissions;
    }
    addUser({ input: userPayload, token });
    setConfirmStep(false);
  };

  const handleCancel = () => {
    setConfirmStep(false);
  };

  if (!mounted) return null;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-[9999] transform ${entered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out flex flex-col`}
      >
        {/* Header — firma drawer ile aynı stil */}
        <div className="flex-none bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5h4a1 1 0 011 1v5a1 1 0 01-1 1H6a1 1 0 01-1-1v-5a1 1 0 011-1h4z" />
              </svg>
              <h2 className="text-lg sm:text-xl font-bold">Yeni Kullanıcı Oluştur</h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/20 transition-colors" aria-label="Kapat">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <p className="text-blue-100 mt-2 text-sm">Yeni kullanıcı bilgilerini girerek firmaya ekleyin</p>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {resultAdd.isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 flex-1">
              <Loader Visible={true} Width={48} Height={48} Color="#3B82F6" />
              <p className="mt-4 text-sm text-gray-600">Kullanıcı oluşturuluyor...</p>
            </div>
          ) : confirmStep ? (
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-2">İşlemi onaylayın</h3>
              <p className="text-sm text-gray-600 mb-4">Aşağıdaki bilgilerle yeni kullanıcı oluşturulacak. Onaylıyor musunuz?</p>
              <div className="grid grid-cols-2 gap-3 p-4 bg-white rounded-xl border border-gray-200 text-sm">
                <div><span className="text-gray-500">Firma Tipi</span><p className="font-medium text-gray-800">{ROLE_DISPLAY_NAME[input.role] || input.role}</p></div>
                {input.subRole && <div><span className="text-gray-500">Rol</span><p className="font-medium text-gray-800">{SUBROLE_DISPLAY_NAME[input.subRole] || input.subRole}</p></div>}
                <div><span className="text-gray-500">Firma</span><p className="font-medium text-gray-800">{input.firmName}</p></div>
                <div><span className="text-gray-500">İsim</span><p className="font-medium text-gray-800">{input.name}</p></div>
                <div><span className="text-gray-500">Soyisim</span><p className="font-medium text-gray-800">{input.lastName}</p></div>
                <div><span className="text-gray-500">E-posta</span><p className="font-medium text-gray-800">{input.email}</p></div>
                <div><span className="text-gray-500">Telefon</span><p className="font-medium text-gray-800">{input.tel}</p></div>
                <div className="col-span-2"><span className="text-gray-500">Durum</span><p className="font-medium text-gray-800">{input.isActive ? "Aktif" : "Pasif"}</p></div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={handleCancel} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">Vazgeç</button>
                <button type="button" onClick={handleSubmit} className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Onayla ve Oluştur
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0 p-6">
              <UserForm
                initialFValues={input}
                handleOpenModal={(values) => {
                  setInput(values);
                  setConfirmStep(true);
                }}
                token={token}
                isCreate={true}
                firmTypeFromFirm={firmTypeFromFirm}
                fixedFirmName={defaultFirm?.firmName}
                fixedFirmId={defaultFirm?._id}
                onCancel={onClose}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Drawer for editing a user
export const UserEditDrawer = ({ open, onClose, token, userId, refetch }) => {
  const {
    data: userResp,
    isLoading: loadingGet,
    refetch: refetchUser,
  } = useGetUserQuery({
    id: userId,
    token,
  });
  const { firms } = useFirmData(token);
  const [input, setInput] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);
  const { showSuccess, showError } = useAlert();
  const [updateUser, resultUpdate] = useUpdateUserMutation();

  const firmTypeFromFirm = useMemo(() => {
    if (!userResp?.data?.user?.firm || !firms) return null;
    const userFirmId = userResp.data.user.firm._id || userResp.data.user.firm;
    const matched = firms.find((f) => f._id === userFirmId);
    return matched?.firmType || null;
  }, [userResp, firms]);

  useEffect(() => {
    if (userResp?.data?.user) {
      const u = userResp.data.user;
      setInput({
        role: u.role || "",
        firmName: u.firm?.firmName || "",
        name: u.name,
        lastName: u.lastName,
        email: u.email,
        tel: u.tel,
        cardId: u.cardId || "",
        nfcCardId: u.nfcCardId || "",
        isActive: u.isActive !== undefined ? u.isActive : true,
        subRole: u.subRole || "",
        roleTemplate: u.roleTemplate ? String(u.roleTemplate) : "",
        autoReportMailConfig: u.autoReportMailConfig || {
          daily: { enabled: false, sendTime: "08:00" },
          weekly: { enabled: false, sendTime: "08:00", daysOfWeek: [] },
          monthly: { enabled: false, sendTime: "08:00", dayOfMonth: 1 },
        },
      });
    }
  }, [userResp]);

  useEffect(() => {
    if (resultUpdate.isSuccess) {
      showSuccess("Kullanıcı güncellendi!");
      refetchUser();
      refetch();
      onClose();
    } else if (resultUpdate.isError) {
      showError("Güncelleme hatası.");
    }
  }, [resultUpdate.isSuccess, resultUpdate.isError, onClose, refetch, refetchUser, showSuccess, showError]);

  const handleSubmit = () => {
    // Firma değişikliği bu drawer'dan yapılamaz — firmName payload'a konmaz.
    // templatePermissions da gönderilmez; şablon değiştiyse server izinleri şablondan uygular.
    const { firmName, templatePermissions, ...updatePayload } = input;
    updateUser({
      input: {
        ...updatePayload,
        id: userId,
        // role ve subRole artık doğrudan sistem değerleri
        autoReportMailConfig: input.autoReportMailConfig || {},
      },
      token,
    });
    setConfirmStep(false);
  };

  const handleCancel = () => {
    setConfirmStep(false);
  };

  return (
    <Drawer open={open} onClose={onClose} title="Kullanıcı Düzenle">
      <div className="relative">
        {loadingGet || resultUpdate.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader Visible={true} Width={48} Height={48} Color="#6366f1" />
          </div>
        ) : !input ? (
          <div className="p-8 text-center text-slate-500">
            <p>Kullanıcı bilgileri yüklenemedi.</p>
          </div>
        ) : confirmStep ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-1">
                Değişiklikleri onaylayın
              </h3>
              <p className="text-sm text-slate-600">
                Kullanıcı bilgileri aşağıdaki şekilde güncellenecek. Onaylıyor musunuz?
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-2 gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Firma Tipi</p>
                  <p className="font-medium text-slate-800 mt-0.5">{ROLE_DISPLAY_NAME[input.role] || input.role}</p>
                </div>
                {input.subRole && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Rol</p>
                    <p className="font-medium text-slate-800 mt-0.5">{SUBROLE_DISPLAY_NAME[input.subRole] || input.subRole}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Firma</p>
                  <p className="font-medium text-slate-800 mt-0.5">{input.firmName}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">İsim</p>
                  <p className="font-medium text-slate-800 mt-0.5">{input.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Soyisim</p>
                  <p className="font-medium text-slate-800 mt-0.5">{input.lastName}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">E-posta</p>
                  <p className="font-medium text-slate-800 mt-0.5">{input.email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Telefon</p>
                  <p className="font-medium text-slate-800 mt-0.5">{input.tel}</p>
                </div>
                {input.cardId && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Kart ID</p>
                    <p className="font-medium text-slate-800 mt-0.5">{input.cardId}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Durum</p>
                  <p className="font-medium text-slate-800 mt-0.5">{input.isActive ? "Aktif" : "Pasif"}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors shadow-sm"
              >
                Onayla ve Güncelle
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col min-h-0 flex-1">
            <UserForm
              initialFValues={input}
              handleOpenModal={(values) => {
                setInput(values);
                setConfirmStep(true);
              }}
              token={token}
              firmTypeFromFirm={firmTypeFromFirm}
              // Edit modunda firma değiştirilemez — firma salt-okunur gösterilir,
              // firmId ise rol şablonlarının çekilebilmesi için gerekir.
              fixedFirmName={userResp?.data?.user?.firm?.firmName || input.firmName}
              fixedFirmId={userResp?.data?.user?.firm?._id || userResp?.data?.user?.firm}
              onCancel={onClose}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};
